import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaArrowLeft, FaExchangeAlt } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { budgetService } from '../../../services/apiService';

const BudgetCompare = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [baseBudget, setBaseBudget] = useState(null);
  const [budgetList, setBudgetList] = useState([]);
  const [selectedBudgetId, setSelectedBudgetId] = useState('');
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch the base budget
        const budgetData = await budgetService.getBudgetById(id);
        setBaseBudget(budgetData);
        
        // Fetch all budgets for comparison selection
        const allBudgets = await budgetService.getAllBudgets();
        
        // Ensure we have an array
        const budgetsArray = Array.isArray(allBudgets) ? allBudgets : [];
        
        // Filter out the current budget from comparison list
        setBudgetList(budgetsArray.filter(budget => budget._id !== id));
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load budget data');
        toast.error('Failed to load budget data for comparison');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  const handleCompare = async () => {
    if (!selectedBudgetId) {
      toast.error('Please select a budget to compare with');
      return;
    }
    
    try {
      setComparing(true);
      const comparisonData = await budgetService.compareBudgets(id, selectedBudgetId);
      setComparisonData(comparisonData);
    } catch (error) {
      console.error('Error comparing budgets:', error);
      toast.error('Failed to compare budgets');
    } finally {
      setComparing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const getChangePercentage = (baseValue, comparisonValue) => {
    if (baseValue === 0) {
      // If base is 0, and comparison has a value, it's a "new" item
      return comparisonValue > 0 ? 'NEW' : '0%';
    }
    
    const percentage = ((comparisonValue - baseValue) / Math.abs(baseValue)) * 100;
    return `${percentage > 0 ? '+' : ''}${percentage.toFixed(1)}%`;
  };

  if (loading) {
    return <div className="flex justify-center p-8">Loading budget data...</div>;
  }

  if (error || !baseBudget) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          <p>{error || 'Budget not found'}</p>
          <Link to="/budgeting" className="text-blue-500 hover:underline mt-2 inline-block">
            Back to Budgets
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex items-center mb-6">
        <Link to={`/budgeting/${baseBudget._id}`} className="text-blue-500 hover:text-blue-700 mr-4">
          <FaArrowLeft className="inline mr-1" /> Back to Budget
        </Link>
        <h1 className="text-2xl font-semibold">Compare Budgets</h1>
      </div>
      
      {/* Budget Selection */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
        <h2 className="text-lg font-medium mb-4">Select Budgets to Compare</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Base Budget
            </label>
            <input
              type="text"
              value={baseBudget.name}
              readOnly
              className="w-full p-2 border border-gray-300 rounded bg-gray-50"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Comparison Budget
            </label>
            <select
              value={selectedBudgetId}
              onChange={(e) => setSelectedBudgetId(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
            >
              <option value="">Select a budget to compare</option>
              {budgetList.map((budget) => (
                <option key={budget._id} value={budget._id}>
                  {budget.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="md:col-span-2 flex justify-center">
            <button
              onClick={handleCompare}
              disabled={!selectedBudgetId || comparing}
              className="flex items-center bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
            >
              <FaExchangeAlt className="mr-2" />
              {comparing ? 'Comparing...' : 'Compare Budgets'}
            </button>
          </div>
        </div>
      </div>
      
      {/* Comparison Results */}
      {comparisonData && (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-4 border-b">
            <h2 className="text-xl font-semibold">Comparison Results</h2>
            <p className="text-gray-600">
              Comparing {comparisonData.base.name} with {comparisonData.comparison.name}
            </p>
          </div>
          
          {/* Summary */}
          <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-md font-medium text-blue-700">Total Planned Expenses</h3>
              <div className="flex justify-between items-center mt-2">
                <div>
                  <p className="text-sm text-gray-600">Base</p>
                  <p className="text-lg font-bold">{formatCurrency(comparisonData.base.totalPlannedExpenses)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Comparison</p>
                  <p className="text-lg font-bold">{formatCurrency(comparisonData.comparison.totalPlannedExpenses)}</p>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t">
                <div className="flex justify-between">
                  <span className="text-sm">Difference</span>
                  <span className={`font-medium ${comparisonData.comparison.totalPlannedExpenses > comparisonData.base.totalPlannedExpenses ? 'text-red-600' : 'text-green-600'}`}>
                    {formatCurrency(comparisonData.comparison.totalPlannedExpenses - comparisonData.base.totalPlannedExpenses)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">% Change</span>
                  <span className={`font-medium ${comparisonData.comparison.totalPlannedExpenses > comparisonData.base.totalPlannedExpenses ? 'text-red-600' : 'text-green-600'}`}>
                    {getChangePercentage(comparisonData.base.totalPlannedExpenses, comparisonData.comparison.totalPlannedExpenses)}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-md font-medium text-green-700">Total Planned Income</h3>
              <div className="flex justify-between items-center mt-2">
                <div>
                  <p className="text-sm text-gray-600">Base</p>
                  <p className="text-lg font-bold">{formatCurrency(comparisonData.base.totalPlannedIncome)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Comparison</p>
                  <p className="text-lg font-bold">{formatCurrency(comparisonData.comparison.totalPlannedIncome)}</p>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t">
                <div className="flex justify-between">
                  <span className="text-sm">Difference</span>
                  <span className={`font-medium ${comparisonData.comparison.totalPlannedIncome > comparisonData.base.totalPlannedIncome ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(comparisonData.comparison.totalPlannedIncome - comparisonData.base.totalPlannedIncome)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">% Change</span>
                  <span className={`font-medium ${comparisonData.comparison.totalPlannedIncome > comparisonData.base.totalPlannedIncome ? 'text-green-600' : 'text-red-600'}`}>
                    {getChangePercentage(comparisonData.base.totalPlannedIncome, comparisonData.comparison.totalPlannedIncome)}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="text-md font-medium text-purple-700">Net Budget (Income - Expenses)</h3>
              <div className="flex justify-between items-center mt-2">
                <div>
                  <p className="text-sm text-gray-600">Base</p>
                  <p className="text-lg font-bold">
                    {formatCurrency(comparisonData.base.totalPlannedIncome - comparisonData.base.totalPlannedExpenses)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Comparison</p>
                  <p className="text-lg font-bold">
                    {formatCurrency(comparisonData.comparison.totalPlannedIncome - comparisonData.comparison.totalPlannedExpenses)}
                  </p>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t">
                <div className="flex justify-between">
                  <span className="text-sm">Difference</span>
                  <span className={`font-medium ${
                    (comparisonData.comparison.totalPlannedIncome - comparisonData.comparison.totalPlannedExpenses) > 
                    (comparisonData.base.totalPlannedIncome - comparisonData.base.totalPlannedExpenses) 
                      ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatCurrency(
                      (comparisonData.comparison.totalPlannedIncome - comparisonData.comparison.totalPlannedExpenses) -
                      (comparisonData.base.totalPlannedIncome - comparisonData.base.totalPlannedExpenses)
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Category Breakdown */}
          <div className="p-4">
            <h3 className="text-lg font-medium mb-4">Category Comparison</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3 text-left text-gray-700">Category</th>
                    <th className="p-3 text-right text-gray-700">Base Planned</th>
                    <th className="p-3 text-right text-gray-700">Comparison Planned</th>
                    <th className="p-3 text-right text-gray-700">Difference</th>
                    <th className="p-3 text-right text-gray-700">% Change</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.categories.map((category, index) => (
                    <tr key={category.id || index} className="border-b hover:bg-gray-50">
                      <td className="p-3">{category.name}</td>
                      <td className="p-3 text-right">
                        {formatCurrency(category.base.plannedAmount)}
                      </td>
                      <td className="p-3 text-right">
                        {formatCurrency(category.comparison.plannedAmount)}
                      </td>
                      <td className="p-3 text-right">
                        <span className={
                          category.difference.planned > 0 ? 'text-red-600' : 
                          category.difference.planned < 0 ? 'text-green-600' : 'text-gray-600'
                        }>
                          {formatCurrency(category.difference.planned)}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <span className={
                          category.difference.planned > 0 ? 'text-red-600' : 
                          category.difference.planned < 0 ? 'text-green-600' : 'text-gray-600'
                        }>
                          {getChangePercentage(category.base.plannedAmount, category.comparison.plannedAmount)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetCompare; 